import { isDateType } from "@evala/odata-metadata";
import BindingContext, { IEntity } from "@odata/BindingContext";
import { setBoundValue } from "@odata/Data.utils";
import { logger } from "@utils/log";
import dayjs, { Dayjs } from "dayjs";

import { getEvalaVersion } from "../../global.utils";
import DateType, { DateFormat, getLocalTimeDayjs, getUtcDayjs } from "../../types/Date";
import { FormStorage } from "./FormStorage";

const NEW_ENTITY_DRAFTS_STORAGE_KEY = "LocalDraftsData";

interface INewEntityDraftRecord {
    appVersion: string;
    companyId: string | number | null;
    entityType: string;
    savedAt: string;
    data: IEntity;
}

type TNewEntityDrafts = Record<string, INewEntityDraftRecord>;

export function canPersistUnsavedEntityData(storage: FormStorage): boolean {
    const isFullyLoaded = storage.loaded && !storage.loading && storage.afterLoaded;
    const { saveDraftLocally } = storage.data.definition ?? {};
    return !!saveDraftLocally && isFullyLoaded && storage.data.bindingContext?.isNew() && storage.isDirty();
}

export function getStorageEntityType(storage: FormStorage): string | undefined {
    return storage.data.bindingContext?.getEntityType()?.getName();
}

export function formatUnsavedNewEntityDataDateTime(savedAt: string): string {
    return DateType.format(getLocalTimeDayjs(savedAt), DateFormat.dateAndTime, { local: true });
}

/**
 * =========== LocalStorage drafts parsing and saving functions ===========
 */
export function getNewEntityDrafts(): TNewEntityDrafts {
    const storageValue = localStorage.getItem(NEW_ENTITY_DRAFTS_STORAGE_KEY);

    if (!storageValue) {
        return {};
    }

    try {
        return JSON.parse(storageValue) as TNewEntityDrafts;
    } catch {
        localStorage.removeItem(NEW_ENTITY_DRAFTS_STORAGE_KEY);
        return {};
    }
}

export function setNewEntityDrafts(drafts: TNewEntityDrafts): void {
    if (Object.keys(drafts).length === 0) {
        localStorage.removeItem(NEW_ENTITY_DRAFTS_STORAGE_KEY);
        return;
    }

    localStorage.setItem(NEW_ENTITY_DRAFTS_STORAGE_KEY, JSON.stringify(drafts));
}

export function removeDraftsForEntityType(storage: FormStorage): void {
    const entityType = getStorageEntityType(storage);
    if (!entityType) {
        return;
    }

    const drafts = getNewEntityDrafts();
    delete drafts[entityType];
    setNewEntityDrafts(drafts);
}

export function removeNonPersistableData(value: unknown): unknown {
    if (value === null || value === undefined) {
        return value;
    }

    if (Array.isArray(value)) {
        return value.map(v => removeNonPersistableData(v));
    }

    if (dayjs.isDayjs(value)) {
        value = (value as Dayjs).toDate();
    }

    if (value instanceof Date) {
        return value;
    }

    if (typeof value === "object") {
        const result: Record<string, unknown> = {};

        for (const [key, nestedValue] of Object.entries(value)) {
            if (key === "TemporaryGuid" || BindingContext.isLocalContextPath(key)) {
                continue;
            }

            result[key] = removeNonPersistableData(nestedValue);
        }

        return result;
    }

    return value;
}

export function persistUnsavedNewEntityData(storage: FormStorage, data: IEntity): void {
    const entityType = getStorageEntityType(storage);

    if (!entityType) {
        return;
    }

    try {
        const drafts = getNewEntityDrafts();
        drafts[entityType] = {
            appVersion: getEvalaVersion(),
            companyId: storage.context?.getCompanyId() ?? "",
            entityType,
            savedAt: getLocalTimeDayjs().toISOString(),
            data
        };
        setNewEntityDrafts(drafts);
    } catch (e) {
        logger.error("Failed to persist unsaved new entity data", e instanceof Error ? e : undefined, { entityType });
    }
}

/**
 * Transform saved data to correct data classes (e.g. date strings to date objects) and set them to the draft data.
 * @param storage
 * @param draft
 */
function parseDraftData(storage: FormStorage, draft: IEntity): void {
    BindingContext.each(draft, storage.data.bindingContext, (val, bc) => {
        let newVal = val;
        if (isDateType(bc?.getProperty())) {
            if (typeof newVal === "object" && dayjs.isDayjs(newVal)) {
                // serialized dayjs object
                newVal = getUtcDayjs((newVal as unknown as { $d: string }).$d).toDate();
            }
            // standard date string or timestamp
            if (typeof newVal === "string") {
                newVal = getUtcDayjs(newVal).toDate();
            } else if (newVal instanceof Date) {
                // keep it as instance of Date
            } else if (typeof newVal === "number") {
                // eslint-disable-next-line custom-rules/no-new-date
                newVal = new Date(newVal);
            } else {
                newVal = null;
            }
        }

        if (newVal !== val) {
            setBoundValue({
                bindingContext: bc,
                data: draft,
                newValue: newVal,
                dataBindingContext: storage.data.bindingContext,
                preventCloning: true
            });
        }
        return true;
    });
}

export function getUnsavedNewEntityData(storage: FormStorage): false | INewEntityDraftRecord {
    const entityType = getStorageEntityType(storage);

    if (!entityType) {
        return false;
    }

    const drafts = getNewEntityDrafts();
    const draft = drafts[entityType];

    if (!draft) {
        return false;
    }

    if (draft.appVersion !== getEvalaVersion() || draft.companyId !== storage.context.getCompanyId()) {
        removeDraftsForEntityType(storage);
        return false;
    }

    // transform date strings to date objects
    parseDraftData(storage, draft.data);

    return draft;
}

export function restoreUnsavedNewEntityData(storage: FormStorage, draft: INewEntityDraftRecord): void {
    storage.store({
        entity: draft.data as unknown as IEntity,
        origEntity: {},
        alert: null
    });
    storage.refreshSync(true);
}

